import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Switch, FormControlLabel, TextField, Button, Grid,
  Alert, Divider, InputAdornment, Collapse
} from '@mui/material';
import { ReceiptLong as InvoiceIcon } from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';

const InvoiceSettingsSection = ({ canManage = true }) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState({
    numberPrefix: 'R-', monthlyRunDay: 1, defaultPaymentTermsDays: 14,
    iban: '', bic: '', holder: '', footerNote: ''
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'appSettings', 'invoices'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setEnabled(d.invoicesEnabled === true);
      setForm({
        numberPrefix: d.numberPrefix || 'R-',
        monthlyRunDay: d.monthlyRunDay || 1,
        defaultPaymentTermsDays: d.defaultPaymentTermsDays ?? 14,
        iban: d.bank?.iban || '', bic: d.bank?.bic || '', holder: d.bank?.holder || '',
        footerNote: d.footerNote || ''
      });
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const setField = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleToggle = async () => {
    setBusy(true); setFeedback(null);
    try {
      await httpsCallable(functions, 'configureInvoices')({ enabled: !enabled });
      setFeedback({ severity: 'success', message: !enabled ? 'Rechnungserstellung aktiviert.' : 'Rechnungserstellung deaktiviert.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.message || 'Einstellung konnte nicht gespeichert werden.' });
    } finally { setBusy(false); }
  };

  const handleSave = async () => {
    setSaving(true); setFeedback(null);
    try {
      await httpsCallable(functions, 'configureInvoices')({
        numberPrefix: form.numberPrefix,
        monthlyRunDay: Number(form.monthlyRunDay) || 1,
        defaultPaymentTermsDays: Number(form.defaultPaymentTermsDays) || 14,
        bank: { iban: form.iban, bic: form.bic, holder: form.holder },
        footerNote: form.footerNote
      });
      setFeedback({ severity: 'success', message: 'Rechnungs-Einstellungen gespeichert.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.message || 'Speichern fehlgeschlagen.' });
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, opacity: canManage ? 1 : 0.6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InvoiceIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={600}>Rechnungen (Firmenkunden)</Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Firmen-, Steuer- und Versanddaten (Anschrift, Steuernummer, SMTP) werden
        aus der Einrichtung unter <strong>„Quittungen"</strong> übernommen — bitte
        dort zuerst vervollständigen. Hier nur das Rechnungsspezifische.
      </Alert>

      {feedback && <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>{feedback.message}</Alert>}

      <FormControlLabel
        control={<Switch checked={enabled} onChange={handleToggle} disabled={!canManage || busy} />}
        label="Rechnungen für Firmenkunden erstellen (monatliche Sammelrechnung + Einzelrechnung)"
      />

      <Collapse in>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Rechnungsnummer-Präfix" value={form.numberPrefix} onChange={setField('numberPrefix')} disabled={!canManage} placeholder="R-" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="number" label="Monatslauf am Tag" value={form.monthlyRunDay} onChange={setField('monthlyRunDay')} disabled={!canManage} inputProps={{ min: 1, max: 28 }} helperText="1–28" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="number" label="Zahlungsziel (Tage)" value={form.defaultPaymentTermsDays} onChange={setField('defaultPaymentTermsDays')} disabled={!canManage} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Bankverbindung (für den Zahlungshinweis auf der Rechnung)</Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth size="small" label="IBAN" value={form.iban} onChange={setField('iban')} disabled={!canManage}
              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="BIC" value={form.bic} onChange={setField('bic')} disabled={!canManage} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Kontoinhaber" value={form.holder} onChange={setField('holder')} disabled={!canManage} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Fußzeile / Zahlungshinweis (optional)" value={form.footerNote} onChange={setField('footerNote')} disabled={!canManage} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleSave} disabled={!canManage || saving}>
              {saving ? 'Speichert…' : 'Rechnungs-Einstellungen speichern'}
            </Button>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default InvoiceSettingsSection;
